import type { PurchaseOrder, VendorPayment } from "@/features/purchase-orders/types";
import { sumAppliedAmount } from "@/lib/credit-application-balances";
import { roundToCents, toCents } from "@/lib/money";

interface VendorCreditApplicationTotalRow {
  readonly amount_applied?: unknown;
  readonly is_reversal?: unknown;
}

interface PurchaseOrderTotalsInput {
  readonly payments: readonly Pick<VendorPayment, "amount">[];
  readonly purchaseOrder: PurchaseOrder;
  readonly vendorCreditApplications?: readonly VendorCreditApplicationTotalRow[];
}

type PurchaseOrderTotals = Pick<
  PurchaseOrder,
  "balance" | "is_paid" | "payment_status" | "total_paid"
>;

function toFiniteNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function paymentStatus(totalAmount: number, totalPaid: number): "paid" | "partial" | "unpaid" {
  const amountCents = toCents(totalAmount);
  const paidCents = toCents(totalPaid);

  if (paidCents >= amountCents && amountCents > 0) {
    return "paid";
  }
  if (paidCents > 0) {
    return "partial";
  }
  return "unpaid";
}

/** Recomputes purchase-order payment totals, including vendor-credit applications. */
export function purchaseOrderTotals({
  purchaseOrder,
  payments,
  vendorCreditApplications = [],
}: PurchaseOrderTotalsInput): PurchaseOrderTotals {
  const totalAmount = toFiniteNumber(purchaseOrder.total_amount);
  const paymentTotal = payments.reduce((sum, payment) => sum + toFiniteNumber(payment.amount), 0);
  const creditApplied = sumAppliedAmount(vendorCreditApplications);
  const totalPaid = roundToCents(paymentTotal + creditApplied);
  const status = paymentStatus(totalAmount, totalPaid);

  return {
    balance: Math.max(0, roundToCents(totalAmount - totalPaid)),
    is_paid: status === "paid",
    payment_status: status,
    total_paid: totalPaid,
  };
}
